import { useEffect } from 'react';
import { Dialogs } from '@/dialogs/Dialogs';
import { clearUrl, restoreFromUrl, startUrlSync } from '@/routing';
import { BoardScreen } from '@/screens/BoardScreen';
import { CustomerScreen, CustomersScreen } from '@/screens/CustomersScreen';
import { DashboardScreen } from '@/screens/DashboardScreen';
import { ItemsScreen } from '@/screens/ItemsScreen';
import { JobScreen } from '@/screens/JobScreen';
import { JobsScreen } from '@/screens/JobsScreen';
import { CheckingSession, LoginScreen } from '@/screens/LoginScreen';
import { ScheduleScreen } from '@/screens/ScheduleScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';
import { unsaved, useApp } from '@/store/useApp';
import { Shell } from '@/ui/Shell';
import { Toast } from '@/ui/controls';

export default function App() {
  const screen = useApp((s) => s.screen);
  const theme = useApp((s) => s.theme);
  const toast = useApp((s) => s.toast);
  const authStatus = useApp((s) => s.authStatus);
  const loadSession = useApp((s) => s.loadSession);

  // index.html paints the stored theme before first render; this keeps the
  // attribute in step if the store's value ever diverges from the DOM.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Ask the server who we are, once. The cookies are httpOnly, so this is the
  // only way to find out.
  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  // The address bar is only meaningful once there is a session behind it: the
  // path is read back on sign-in, kept in step while signed in, and dropped on
  // the way out.
  useEffect(() => {
    if (authStatus === 'signed-in') {
      restoreFromUrl();
      return startUrlSync();
    }
    if (authStatus === 'signed-out') clearUrl();
    return undefined;
  }, [authStatus]);

  // Leaving the app altogether — a reload, a closed tab, a typed address — is
  // the one navigation the app cannot draw its own dialog over. The browser
  // draws its own instead, in its own words; all it wants is to be told there
  // is something to lose. The listener reads the store when it fires rather
  // than subscribing, so typing into a form re-registers nothing.
  useEffect(() => {
    if (authStatus !== 'signed-in') return undefined;
    const onLeave = (e: BeforeUnloadEvent) => {
      if (!unsaved(useApp.getState())) return;
      e.preventDefault();
      // Chrome and Safari still want this; the string itself is never shown.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [authStatus]);

  if (authStatus === 'checking') return <CheckingSession />;

  if (authStatus === 'signed-out') {
    return (
      <>
        <LoginScreen />
        <Toast message={toast} />
      </>
    );
  }

  return (
    <Shell>
      {screen === 'dashboard' ? <DashboardScreen /> : null}
      {screen === 'board' ? <BoardScreen /> : null}
      {screen === 'job' ? <JobScreen /> : null}
      {screen === 'items' ? <ItemsScreen /> : null}
      {screen === 'schedule' ? <ScheduleScreen /> : null}
      {screen === 'jobs' ? <JobsScreen /> : null}
      {screen === 'customers' ? <CustomersScreen /> : null}
      {screen === 'customer' ? <CustomerScreen /> : null}
      {screen === 'settings' ? <SettingsScreen /> : null}
      <Dialogs />
      <Toast message={toast} />
    </Shell>
  );
}
